import { Injectable, inject } from '@angular/core';
import {
  Episode,
  EpisodeAccessType,
  EpisodesResult,
  GetArtistProgramsDocument,
  GetArtistProgramsQueryVariables,
  GetContinueWatchingDocument,
  GetContinueWatchingQueryVariables,
  GetExclusiveProgramsDocument,
  GetExclusiveProgramsQueryVariables,
  GetProgramArtistRolesDocument,
  GetProgramArtistRolesQueryVariables,
  GetProgramByIdDocument,
  GetProgramByIdQueryVariables,
  GetProgramByUidDocument,
  GetProgramByUidQueryVariables,
  GetProgramInMiniDialogDocument,
  GetProgramInMiniDialogQueryVariables,
  GetProgramResumeByUidDocument,
  GetProgramResumeByUidQueryVariables,
  GetProgramsBookmarkedDocument,
  GetProgramsBookmarkedQueryVariables,
  GetProgramsByIDsDocument,
  GetProgramsByIDsQueryVariables,
  GetProgramsByInterestDocument,
  GetProgramsByInterestQueryVariables,
  GetProgramsByTagIdDocument,
  GetProgramsByTagIdQueryVariables,
  GetRelatedProgramsDocument,
  GetRelatedProgramsQueryVariables,
  GetSeasonEpisodesDocument,
  GetSeasonEpisodesQueryVariables,
  InterestAction,
  ProgramArtistRolesResult,
  ProgramResult,
  ProgramResumeResult,
  ProgramResumesResult,
  ProgramWithSeasonsResult,
  ToggleBookmarkProgramDocument,
  ToggleBookmarkProgramMutationVariables,
  ToggleBookmarkProgramResult,
} from '@shared/graphql/api.generated';
import { GraphqlClients, GraphqlService } from '@shared/graphql/graphql.service';
import { GeneralService } from './general.service';
import { TokenService } from './token.service';

@Injectable()
export class ProgramService {
  private readonly graphqlService = inject(GraphqlService);
  private readonly tokenService = inject(TokenService);
  private readonly generalService = inject(GeneralService);

  getProgramDetailsInMiniDialogByUid(uid: string) {
    return this.graphqlService.makeQuery<
      ProgramWithSeasonsResult,
      GetProgramInMiniDialogQueryVariables
    >(
      GraphqlClients.api,
      GetProgramInMiniDialogDocument,
      {
        uid,
      },
      false,
    );
  }

  getProgramByUid(uid: string) {
    return this.graphqlService.makeQuery<ProgramWithSeasonsResult, GetProgramByUidQueryVariables>(
      GraphqlClients.api,
      GetProgramByUidDocument,
      { uid },
    );
  }

  getProgramById(id: string) {
    return this.graphqlService.makeQuery<ProgramWithSeasonsResult, GetProgramByIdQueryVariables>(
      GraphqlClients.api,
      GetProgramByIdDocument,
      { id },
    );
  }

  getSeasonEpisodes(seasonID: string) {
    return this.graphqlService.makeQuery<EpisodesResult, GetSeasonEpisodesQueryVariables>(
      GraphqlClients.api,
      GetSeasonEpisodesDocument,
      {
        seasonID,
      },
    );
  }

  getArtistPrograms(artistID: string, page = 1) {
    return this.graphqlService.makeQuery<ProgramResult, GetArtistProgramsQueryVariables>(
      GraphqlClients.api,
      GetArtistProgramsDocument,
      {
        artistID,
        page,
      },
    );
  }

  toggleBookmark(programID: string) {
    return this.graphqlService.makeMutation<
      ToggleBookmarkProgramResult,
      ToggleBookmarkProgramMutationVariables
    >(GraphqlClients.api, ToggleBookmarkProgramDocument, {
      programID,
    });
  }

  getTagPrograms(TagID: string, page = 1) {
    return this.graphqlService.makeQuery<ProgramResult, GetProgramsByTagIdQueryVariables>(
      GraphqlClients.api,
      GetProgramsByTagIdDocument,
      {
        TagID,
        page,
      },
    );
  }

  getRelatedPrograms(programID: string, page = 1) {
    return this.graphqlService.makeQuery<ProgramResult, GetRelatedProgramsQueryVariables>(
      GraphqlClients.api,
      GetRelatedProgramsDocument,
      {
        programID,
        page,
      },
    );
  }

  getResumedProgram(uid: string) {
    return this.graphqlService.makeQuery<ProgramResumeResult, GetProgramResumeByUidQueryVariables>(
      GraphqlClients.api,
      GetProgramResumeByUidDocument,
      {
        uid,
      },
    );
  }

  getProgramArtistRoles(programID: string) {
    return this.graphqlService.makeQuery<
      ProgramArtistRolesResult,
      GetProgramArtistRolesQueryVariables
    >(GraphqlClients.api, GetProgramArtistRolesDocument, {
      programID,
    });
  }

  getContinueWatching(page = 1) {
    return this.graphqlService.makeQuery<ProgramResumesResult, GetContinueWatchingQueryVariables>(
      GraphqlClients.api,
      GetContinueWatchingDocument,
      {
        page,
      },
    );
  }

  getExclusivePrograms() {
    return this.graphqlService.makeQuery<ProgramResult, GetExclusiveProgramsQueryVariables>(
      GraphqlClients.api,
      GetExclusiveProgramsDocument,
      {},
    );
  }

  getInterestedPrograms(interestAction: InterestAction, page = 1) {
    return this.graphqlService.makeQuery<ProgramResult, GetProgramsByInterestQueryVariables>(
      GraphqlClients.api,
      GetProgramsByInterestDocument,
      {
        interestAction,
        page,
      },
    );
  }

  getBookmarkedPrograms(page = 1) {
    return this.graphqlService.makeQuery<ProgramResult, GetProgramsBookmarkedQueryVariables>(
      GraphqlClients.api,
      GetProgramsBookmarkedDocument,
      { page },
    );
  }

  getProgramsByIds(ids: string[]) {
    return this.graphqlService.makeQuery<ProgramResult, GetProgramsByIDsQueryVariables>(
      GraphqlClients.api,
      GetProgramsByIDsDocument,
      {
        ids: {
          ids: ids,
        },
      },
    );
  }

  canWatchInSiteProgram(episode: Episode): boolean {
    if (
      episode.accessType === EpisodeAccessType.Free ||
      this.tokenService.userInfo()?.activeSubscription.id ||
      this.generalService.freeIsp
    ) {
      return true;
    }
    return false;
  }
}
